import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus,
  Search,
  Edit2,
  Briefcase,
  Eye,
  EyeOff,
  RefreshCw,
  X,
  MapPin,
  Building2,
  Calendar,
  Users,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import apiClient from "@/lib/api";
import {
  getContractTypesLabel,
  getOfferContractTypes,
} from "@/lib/jobOffers";
import AppEmptyState from "@/components/ui/AppEmptyState";
import ConfirmationModal from "@/components/ui/ConfirmationModal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { TESTIDS } from "@/lib/testids";

// Replace hardcoded options with dynamic fetching

const renderInlineMarkdown = (value, keyPrefix) => {
  const parts = [];
  const pattern = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
  let cursor = 0;
  let match = pattern.exec(value);

  while (match) {
    if (match.index > cursor) {
      parts.push(value.slice(cursor, match.index));
    }

    const token = match[0];
    const tokenValue = token.startsWith("**") ? token.slice(2, -2) : token.slice(1, -1);
    if (token.startsWith("**")) {
      parts.push(<strong key={`${keyPrefix}-${match.index}`}>{tokenValue}</strong>);
    } else {
      parts.push(<em key={`${keyPrefix}-${match.index}`}>{tokenValue}</em>);
    }

    cursor = match.index + token.length;
    match = pattern.exec(value);
  }

  if (cursor < value.length) {
    parts.push(value.slice(cursor));
  }

  return parts;
};

const renderMarkdownPreview = (value) => {
  const elements = [];
  const listItems = [];

  const flushList = () => {
    if (!listItems.length) {
      return;
    }

    elements.push(
      <ul key={`list-${elements.length}`} className="list-disc pl-5 space-y-1 mb-4">
        {listItems.map((item, index) => (
          <li key={`list-item-${elements.length}-${index}`}>
            {renderInlineMarkdown(item, `list-item-${elements.length}-${index}`)}
          </li>
        ))}
      </ul>,
    );
    listItems.length = 0;
  };

  value.split(/\r?\n/).forEach((rawLine, index) => {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();

    if (!trimmed) {
      flushList();
      return;
    }

    const listMatch = trimmed.match(/^- (.+)$/);
    if (listMatch) {
      listItems.push(listMatch[1]);
      return;
    }

    flushList();

    const headingMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headingMatch) {
      const headingText = renderInlineMarkdown(headingMatch[2], `heading-${index}`);
      if (headingMatch[1].length === 1) {
        elements.push(<h1 key={`heading-${index}`} className="text-2xl font-semibold mb-4">{headingText}</h1>);
        return;
      }
      if (headingMatch[1].length === 2) {
        elements.push(<h2 key={`heading-${index}`} className="text-xl font-semibold mb-3">{headingText}</h2>);
        return;
      }
      elements.push(<h3 key={`heading-${index}`} className="text-lg font-semibold mb-2">{headingText}</h3>);
      return;
    }

    elements.push(
      <p key={`paragraph-${index}`} className="mb-4">
        {renderInlineMarkdown(trimmed, `paragraph-${index}`)}
      </p>,
    );
  });

  flushList();
  return elements;
};

const createEmptyOfferFormData = () => ({
  title: "",
  department: "",
  location: "",
  contract_types: [],
  salary_range: "",
  description: "",
  detailed_description: "",
  status: "Ouvert",
});

const getFirstDefinedValue = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const normalizeOfferStatus = (offer) => {
  const statusValue = getFirstDefinedValue(offer?.status, offer?.statut);

  if (typeof statusValue === "string" && statusValue.trim()) {
    return statusValue;
  }

  const isActiveValue = getFirstDefinedValue(offer?.is_active, offer?.isActive);
  if (typeof isActiveValue === "boolean") {
    return isActiveValue ? "Ouvert" : "Fermé";
  }

  return "Ouvert";
};

const normalizeOfferToFormData = (offer) => {
  if (!offer) {
    return createEmptyOfferFormData();
  }

  return {
    title: getFirstDefinedValue(offer.title, offer.job_title) || "",
    department: getFirstDefinedValue(offer.department) || "",
    location: getFirstDefinedValue(offer.location) || "",
    contract_types: getOfferContractTypes(offer),
    salary_range: getFirstDefinedValue(offer.salary_range, offer.salaryRange) || "",
    description: getFirstDefinedValue(
      offer.description,
      offer.short_description,
      offer.shortDescription,
    ) || "",
    detailed_description: getFirstDefinedValue(
      offer.detailed_description,
      offer.detailedDescription,
    ) || "",
    status: normalizeOfferStatus(offer),
  };
};

const buildSelectOptions = (options = [], currentValue = "") => {
  const mergedOptions = Array.isArray(options) ? options.filter(Boolean) : [];

  if (currentValue && !mergedOptions.includes(currentValue)) {
    mergedOptions.unshift(currentValue);
  }

  return mergedOptions;
};

const buildMultiSelectOptions = (options = [], currentValues = []) => {
  const mergedOptions = Array.isArray(options) ? options.filter(Boolean) : [];

  currentValues.forEach((value) => {
    if (value && !mergedOptions.includes(value)) {
      mergedOptions.push(value);
    }
  });

  return mergedOptions;
};

const toggleSelectableValue = (values, value) => {
  if (values.includes(value)) {
    return values.filter((item) => item !== value);
  }

  return [...values, value];
};

const ContractTypeMultiSelect = ({
  options,
  selectedValues,
  onChange,
  supportsMultiple = true,
}) => {
  const hasSelectedValues = selectedValues.length > 0;

  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.03] p-3"
      data-testid="admin-offer-contract-type"
    >
      <div className="flex flex-wrap items-center gap-2">
        {options.map((value) => {
          const selected = selectedValues.includes(value);

          return (
            <button
              key={value}
              type="button"
              onClick={() => onChange(toggleSelectableValue(selectedValues, value))}
              className={`rounded-full border px-3 py-1.5 text-sm transition-all ${selected
                ? "border-lenvia-blue/50 bg-lenvia-blue/15 text-white shadow-[0_0_0_1px_rgba(59,130,246,0.15)]"
                : "border-white/10 bg-white/[0.02] text-slate-300 hover:border-white/20 hover:bg-white/[0.05]"
                }`}
            >
              {value}
            </button>
          );
        })}
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {hasSelectedValues ? (
          selectedValues.map((value) => (
            <button
              key={value}
              type="button"
              onClick={() => onChange(selectedValues.filter((item) => item !== value))}
              className="inline-flex items-center gap-1 rounded-full border border-lenvia-blue/30 bg-lenvia-blue/10 px-2.5 py-1 text-xs text-lenvia-blue"
              data-testid="admin-offer-contract-type-chip"
            >
              {value}
              <X className="h-3.5 w-3.5" />
            </button>
          ))
        ) : (
          <span className="text-xs text-slate-500">Aucun type de contrat sélectionné.</span>
        )}

        {hasSelectedValues && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="text-xs font-medium text-slate-400 transition-colors hover:text-white"
            data-testid="admin-offer-contract-types-clear"
          >
            Réinitialiser
          </button>
        )}
      </div>

      {!supportsMultiple && selectedValues.length > 1 && (
        <p className="mt-3 text-xs text-amber-300">
          Airtable est encore en selection unique. L'enregistrement refusera plusieurs valeurs tant que le champ n'est pas migre en multi-select.
        </p>
      )}
    </div>
  );
};

// Rich Text Editor Component (Markdown)
const RichTextEditor = ({ value, onChange, placeholder, textareaTestId }) => {
  const [preview, setPreview] = useState(false);

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 mb-2">
        <button
          type="button"
          onClick={() => setPreview(false)}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${!preview ? "bg-lenvia-blue text-white" : "text-slate-400 hover:text-white"
            }`}
        >
          Édition
        </button>
        <button
          type="button"
          onClick={() => setPreview(true)}
          className={`px-3 py-1 rounded text-xs font-medium transition-colors ${preview ? "bg-lenvia-blue text-white" : "text-slate-400 hover:text-white"
            }`}
        >
          Aperçu
        </button>
        <span className="text-xs text-slate-500 ml-auto">
          Markdown supporté
        </span>
      </div>

      {!preview ? (
        <Textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          data-testid={textareaTestId}
          className="min-h-[200px] bg-white/5 border-white/10 text-white placeholder:text-slate-500 font-mono text-sm"
        />
      ) : (
        <div className="min-h-[200px] p-4 rounded-lg bg-white/5 border border-white/10 prose prose-invert prose-sm max-w-none">
          {value ? (
            renderMarkdownPreview(value)
          ) : (
            <span className="text-slate-500">Aperçu vide</span>
          )}
        </div>
      )}
    </div>
  );
};

// Offer Form Modal
const OfferFormModal = ({ offer, onClose, onSave, options }) => {
  const metadataReady = Boolean(options);
  const [formData, setFormData] = useState(() => normalizeOfferToFormData(offer));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const departmentOptions = buildSelectOptions(options?.departments, formData.department);
  const hasLocationChoices = Array.isArray(options?.locations) && options.locations.length > 0;
  const locationOptions = buildSelectOptions(options?.locations, formData.location);
  const contractTypeOptions = buildMultiSelectOptions(
    options?.contract_types,
    formData.contract_types,
  );

  useEffect(() => {
    setFormData(normalizeOfferToFormData(offer));
  }, [offer]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      const payload = {
        ...formData,
        contract_type: formData.contract_types?.[0] || null,
      };

      if (offer?.id) {
        await apiClient.put(`/admin/offres/${offer.id}`, payload);
      } else {
        await apiClient.post("/admin/offres", payload);
      }
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 overflow-y-auto"
      data-testid="admin-offer-modal"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl glass rounded-2xl p-6 my-8"
      >
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-heading font-bold text-white">
            {offer ? "Modifier l'offre" : "Nouvelle offre"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div
            className="mb-4 p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400 text-sm"
            data-testid="admin-offer-form-error"
          >
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {!metadataReady && (
          <div className="mb-4 p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2 text-amber-300 text-sm">
            <AlertCircle className="w-4 h-4" />
            Les options Airtable sont indisponibles. L'enregistrement est temporairement désactivé.
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label className="text-slate-300">Titre du poste *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Développeur Mobile Senior"
                required
                data-testid="admin-offer-title"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>

            <div>
              <Label className="text-slate-300">Département</Label>
              <Select
                value={formData.department}
                onValueChange={(value) => setFormData({ ...formData, department: value })}
              >
                <SelectTrigger
                  className="bg-white/5 border-white/10 text-white"
                  data-testid="admin-offer-department"
                >
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-white/10">
                  {departmentOptions.map((dept) => (
                    <SelectItem key={dept} value={dept} className="text-white hover:bg-white/10">
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-300">Localisation</Label>
              {hasLocationChoices ? (
                <Select
                  value={formData.location}
                  onValueChange={(value) => setFormData({ ...formData, location: value })}
                >
                  <SelectTrigger
                    className="bg-white/5 border-white/10 text-white"
                    data-testid="admin-offer-location-select"
                  >
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent className="bg-slate-900 border-white/10">
                    {locationOptions.map((loc) => (
                      <SelectItem key={loc} value={loc} className="text-white hover:bg-white/10">
                        {loc}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input
                  value={formData.location}
                  onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  placeholder="Paris, France"
                  data-testid="admin-offer-location-input"
                  className="bg-white/5 border-white/10 text-white"
                />
              )}
            </div>

            <div>
              <Label className="text-slate-300">Type de contrat</Label>
              <ContractTypeMultiSelect
                options={contractTypeOptions}
                selectedValues={formData.contract_types}
                supportsMultiple={options?.contract_types_supports_multiple !== false}
                onChange={(value) => setFormData({ ...formData, contract_types: value })}
              />
            </div>

            <div>
              <Label className="text-slate-300">Fourchette salaire</Label>
              <Input
                value={formData.salary_range}
                onChange={(e) => setFormData({ ...formData, salary_range: e.target.value })}
                placeholder="50-65k€"
                data-testid="admin-offer-salary-range"
                className="bg-white/5 border-white/10 text-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-slate-300">Description courte</Label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Résumé de l'offre en quelques lignes..."
              data-testid="admin-offer-description"
              className="bg-white/5 border-white/10 text-white h-20"
            />
          </div>

          <div>
            <Label className="text-slate-300">Description détaillée</Label>
            <RichTextEditor
              value={formData.detailed_description}
              onChange={(value) => setFormData({ ...formData, detailed_description: value })}
              placeholder="Description complète du poste, missions, profil recherché..."
              textareaTestId="admin-offer-detailed-description"
            />
          </div>

          <div className="flex items-center justify-between pt-4">
            <div className="flex items-center gap-3">
              <Switch
                checked={formData.status === "Ouvert"}
                data-testid="admin-offer-status-switch"
                onCheckedChange={(checked) =>
                  setFormData({ ...formData, status: checked ? "Ouvert" : "Fermé" })
                }
              />
              <Label className="text-slate-300">
                Offre {formData.status === "Ouvert" ? "active" : "inactive"}
              </Label>
            </div>

            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                className="border-white/10 text-slate-300"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={saving || !metadataReady}
                data-testid="admin-offer-submit"
                className="btn-primary"
              >
                {saving ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" />
                    Enregistrement...
                  </span>
                ) : (
                  "Enregistrer"
                )}
              </Button>
            </div>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const AdminOffres = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offerToDelete, setOfferToDelete] = useState(null);
  const [deletingOffer, setDeletingOffer] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);
  const [metadataOptions, setMetadataOptions] = useState(null);
  const [metadataLoading, setMetadataLoading] = useState(true);
  const [metadataError, setMetadataError] = useState(null);
  const canCreateOffer = !metadataLoading && Boolean(metadataOptions);

  const fetchOptions = async () => {
    setMetadataLoading(true);
    setMetadataError(null);

    try {
      const response = await apiClient.get("/admin/metadata/job-options");
      setMetadataOptions(response.data);
    } catch (err) {
      setMetadataOptions(null);
      setMetadataError(
        err.response?.data?.detail ||
        "Les options Airtable sont indisponibles. La création d'offre est désactivée.",
      );
    } finally {
      setMetadataLoading(false);
    }
  };

  const fetchOffers = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get("/admin/offres");
      setOffers(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors du chargement des offres");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOffers();
    fetchOptions();
  }, []);

  const handleRefresh = () => {
    fetchOffers();
    fetchOptions();
  };

  const handleDelete = (offer) => {
    setOfferToDelete(offer);
  };

  const confirmDeleteOffer = async () => {
    if (!offerToDelete) return;
    setDeletingOffer(true);
    try {
      await apiClient.delete(`/admin/offres/${offerToDelete.id}`);
      setSuccessMessage("Offre désactivée avec succès");
      setOfferToDelete(null);
      fetchOffers();
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la désactivation");
    } finally {
      setDeletingOffer(false);
    }
  };

  const handleEdit = (offer) => {
    setEditingOffer(offer);
    setShowModal(true);
  };

  const handleCreate = () => {
    if (!canCreateOffer) {
      return;
    }

    setEditingOffer(null);
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingOffer(null);
  };

  const handleSave = () => {
    handleModalClose();
    setSuccessMessage(editingOffer ? "Offre mise à jour" : "Offre créée avec succès");
    fetchOffers();
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  // Filter offers
  const filteredOffers = offers.filter((offer) => {
    const normalizedSearch = search.toLowerCase();
    const contractTypeLabel = getContractTypesLabel(offer).toLowerCase();
    const matchesSearch =
      String(offer.title || "").toLowerCase().includes(normalizedSearch) ||
      String(offer.department || "").toLowerCase().includes(normalizedSearch) ||
      String(offer.location || "").toLowerCase().includes(normalizedSearch) ||
      contractTypeLabel.includes(normalizedSearch);

    const matchesStatus =
      statusFilter === "all" ||
      (statusFilter === "active" && offer.status === "Ouvert") ||
      (statusFilter === "inactive" && offer.status !== "Ouvert");

    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid={TESTIDS.recruitment.offres.page}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-white">
            Offres d'emploi
          </h1>
          <p className="text-slate-400 mt-1">
            Gérez vos offres de recrutement
          </p>
        </div>
        <Button
          onClick={handleCreate}
          className="btn-primary"
          disabled={!canCreateOffer}
          title={!canCreateOffer ? "Les options Airtable sont indisponibles" : undefined}
          data-testid="admin-offer-create-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle offre
        </Button>
      </div>

      {/* Messages */}
      <AnimatePresence>
        {successMessage && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center gap-2 text-green-400"
            data-testid="admin-offer-success-message"
          >
            <CheckCircle2 className="w-4 h-4" />
            {successMessage}
          </motion.div>
        )}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 flex items-center gap-2 text-red-400"
            data-testid="admin-offer-error-message"
          >
            <AlertCircle className="w-4 h-4" />
            {error}
          </motion.div>
        )}
        {metadataError && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/20 flex items-center gap-2 text-amber-300"
          >
            <AlertCircle className="w-4 h-4" />
            {metadataError}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher une offre..."
            className="pl-10 bg-white/5 border-white/10 text-white"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-40 bg-white/5 border-white/10 text-white">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-white/10">
            <SelectItem value="all" className="text-white">Tous les statuts</SelectItem>
            <SelectItem value="active" className="text-white">Actives</SelectItem>
            <SelectItem value="inactive" className="text-white">Inactives</SelectItem>
          </SelectContent>
        </Select>
        <Button
          onClick={handleRefresh}
          variant="outline"
          className="border-white/10 text-slate-300"
        >
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Offers List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 rounded-full border-2 border-lenvia-blue border-t-transparent animate-spin" />
        </div>
      ) : filteredOffers.length === 0 ? (
        <AppEmptyState
          icon={Briefcase}
          title="Aucune offre trouvée"
          description="Ajoutez une offre pour alimenter le pipeline de recrutement."
          actionLabel="Créer une offre"
          onAction={handleCreate}
          actionDisabled={!canCreateOffer}
          note={!canCreateOffer ? "Création réservée aux administrateurs" : null}
        />
      ) : (
        <div className="space-y-4" data-testid={TESTIDS.recruitment.offres.table}>
          {filteredOffers.map((offer, index) => {
            const contractTypes = getOfferContractTypes(offer);

            return (
              <motion.div
                key={offer.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="glass rounded-xl p-4 sm:p-5"
              >
                <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-semibold text-white truncate">
                        {offer.title}
                      </h3>
                      {offer.status === "Ouvert" ? (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-500/20 text-green-400">
                          Active
                        </span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs bg-slate-500/20 text-slate-400">
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-400">
                      {offer.department && (
                        <span className="flex items-center gap-1">
                          <Building2 className="w-3.5 h-3.5" />
                          {offer.department}
                        </span>
                      )}
                      {offer.location && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {offer.location}
                        </span>
                      )}
                      {contractTypes.length > 0 && (
                        <div className="flex flex-wrap items-center gap-1.5">
                          {contractTypes.map((contractType) => (
                            <span
                              key={`${offer.id}-${contractType}`}
                              className="rounded-full border border-white/10 bg-white/5 px-2 py-0.5 text-xs text-slate-300"
                            >
                              {contractType}
                            </span>
                          ))}
                        </div>
                      )}
                      {offer.publication_date && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3.5 h-3.5" />
                          {new Date(offer.publication_date).toLocaleDateString("fr-FR")}
                        </span>
                      )}
                      <span className="flex items-center gap-1 text-lenvia-blue">
                        <Users className="w-3.5 h-3.5" />
                        {offer.applications_count} candidature{offer.applications_count !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(offer)}
                      className="border-white/10 text-slate-300"
                      data-testid={`admin-offer-edit-button-${offer.id}`}
                    >
                      <Edit2 className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDelete(offer)}
                      className="border-white/10 text-slate-300 hover:text-red-400 hover:border-red-500/30"
                    >
                      {offer.status === "Ouvert" ? (
                        <EyeOff className="w-4 h-4" />
                      ) : (
                        <Eye className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {showModal && (
          <OfferFormModal
            offer={editingOffer}
            onClose={handleModalClose}
            onSave={handleSave}
            options={metadataOptions}
          />
        )}
      </AnimatePresence>

      {/* Deactivation Confirmation Modal replacing window.confirm */}
      <ConfirmationModal
        isOpen={Boolean(offerToDelete)}
        onClose={() => setOfferToDelete(null)}
        title="Désactiver l'offre"
        message={`Confirmez-vous la désactivation de l'offre "${offerToDelete?.title || ""}" ? Elle ne sera plus visible sur le site public.`}
        confirmText="Désactiver l'offre"
        variant="warning"
        isSubmitting={deletingOffer}
        onConfirm={confirmDeleteOffer}
        testId="deactivate-offer-modal"
      />
    </div>
  );
};

export default AdminOffres;
